/**
 * 權限常數表
 * 定義所有頁面的權限 ID 映射關係
 */

/**
 * 編輯權限 UI 顯示的頂層模組（與 injectPrivateRoutesByPermission 對齊）
 * 已下線的模組（會員管理、課程管理）不在此清單
 */
export const ACTIVE_PERMISSION_MODULE_NAMES = [
    '諮詢工作台',
    '資產負債版本',
    '資產負債設置',
    '預約諮詢',
    '角色管理',
    '管理者管理',
    '操作紀錄'
];

export const pagePermissionMap = {
    // 管理者管理
    AdminList: {
        view: 61,
        create: 62,
        update: 63,
        delete: 64,
        assignRole: 65
    },

    // 資產負債設置
    BalanceSheetList: {
        view: 2,
        create: 3,
        update: 4,
        delete: 5,
        copy: 6,
        export: 7
    },

    // 財務管理子頁面權限
    TypeList: {
        view: 10,
        create: 11,
        update: 12,
        delete: 13
    },

    OptionList: {
        view: 15,
        create: 16,
        update: 17,
        delete: 18
    },

    FieldList: {
        view: 25,
        create: 26,
        update: 27,
        delete: 28
    },

    DetailStatusList: {
        view: 25,
        create: 26,
        update: 27,
        delete: 28
    },

    OptionRelationList: {
        view: 29,
        create: 30,
        update: 31,
        delete: 32
    },

    // 資產負債版本
    FinanceVersionList: {
        view: 2,
        create: 3,
        update: 4,
        delete: 5,
        copy: 6,
        export: 7
    },
    FinanceVersionCreate: {
        view: 2,
        create: 3
    },
    FinanceVersionEdit: {
        view: 2,
        update: 4
    },

    // 權限管理
    PermissionManagement: {
        view: 57,
        create: 58,
        update: 59,
        delete: 60
    },

    // 角色管理
    RoleManagement: {
        view: 55,
        create: 56,
        update: 57,
        delete: 58
    },

    // 角色分派
    AssignRole: {
        view: 67,
        create: 68,
        update: 69,
        delete: 70
    },

    ConsultationLevels: {
        view: 81,
        create: 81,
        update: 81
    },

    BookingRules: {
        view: 93,
        update: 93
    },

    /** 管理端：僅可約區間／同時段上限（參數設置 95；與等級設定分開） */
    BookingWindowSettings: {
        view: 95,
        update: 95
    },

    /** 工程師：完整參數設置（預約規則 93） */
    BookingParams: {
        view: 93,
        update: 93
    },

    /** 管理端：可排班日／開班表（96；與預約規則分開） */
    BookingScheduleManage: {
        view: 96,
        update: 96
    },

    /** 工程師：諮詢卷雙開關（預約規則 93） */
    ConsultationTicketSettings: {
        view: 93,
        update: 93
    },


    Consultants: {
        view: 82,
        update: 82
    },

    GrowthMemberLinks: {
        // 預約諮詢.世界樹文明對照，在角色權限裡勾選
        view: 94
    },

    OperationLog: {
        view: 98
    },

    ConsultantSchedule: {
        view: 82,
        update: 82
    },

    ConsultantScheduleEdit: {
        view: 82,
        update: 82
    },

    ConsultantScheduleCalendar: {
        // 92=預約諮詢.排班日曆；82=諮詢師管理（管理員仍可進）
        view: 92,
        viewFallback: 82
    },

    Consultations: {
        view: 83,
        update: 83
    },

    ConsultationWorkspaceDashboard: {
        view: 85
    },
    ConsultationWorkspaceClients: {
        view: 86
    },
    ConsultationWorkspaceClientDetail: {
        view: 86,
        createVersion: 88,
        editVersion: 89,
        completeBooking: 91
    },
    ConsultationWorkspaceClientConsultations: {
        view: 86
    },
    ConsultationWorkspaceVersionView: {
        view: 87
    },
    ConsultationWorkspaceVersionCreate: {
        create: 88
    },
    ConsultationWorkspaceVersionEdit: {
        update: 89
    },
    ConsultationWorkspaceConclusion: {
        view: 87,
        update: 89
    }
};

/**
 * 模組權限映射表
 * 定義每個模組資料夾對應的權限 ID
 */
export const modulePermissionMap = {
    // 管理者管理模組
    'admin-list': {
        folder: 60,
        children: {
            AdminList: [61, 62, 63, 64, 65]
        }
    },

    // 資產負債設置模組（保留原設定，若後端有新對照可再同步）
    finance: {
        folder: 8,
        children: {
            BalanceSheetList: [8],
            TypeList: [9, 10, 11, 12, 13],
            OptionList: [14, 15, 16, 17, 18],
            FieldList: [24, 25, 26, 27, 28],
            DetailStatusList: [29, 30, 31, 32, 33],
            OptionRelationList: [19, 20, 21, 22, 23]
        }
    },

    // 資產負債版本模組
    'finance-version': {
        folder: 1,
        children: {
            FinanceVersionList: [2, 3, 4, 5, 6, 7],
            // 新增頁（不在選單中顯示）
            FinanceVersionCreate: [3],
            // 編輯頁（不在選單中顯示）
            FinanceVersionEdit: [4]
        }
    },

    // 權限管理模組
    permission: {
        folder: 54,
        children: {
            RoleManagement: [55, 56, 57, 58, 59]
        }
    },

    // 角色管理模組（單層結構）
    'role-management': {
        folder: 54,
        children: {
            RoleManagement: [55, 56, 57, 58, 59]
        }
    },

    consultation: {
        folder: 80,
        children: {
            ConsultationLevels: [81],
            BookingRules: [93],
            BookingScheduleManage: [82, 83, 92, 96],
            BookingWindowSettings: [95],
            BookingParams: [93],
            ConsultationTicketSettings: [93],
            Consultants: [82],
            GrowthMemberLinks: [94],
            ConsultantSchedule: [82],
            ConsultantScheduleEdit: [82],
            Consultations: [83]
        }
    },


    'operation-logs': {
        folder: 98,
        children: {
            OperationLog: [98]
        }
    },

    'consultation-workspace': {
        folder: 84,
        children: {
            ConsultationWorkspaceDashboard: [85],
            ConsultationWorkspaceBookings: [85],
            ConsultationWorkspaceClients: [86],
            ConsultationWorkspaceClientDetail: [86, 88, 89, 91],
            ConsultationWorkspaceClientConsultations: [86],
            ConsultationWorkspaceVersionCreate: [88],
            ConsultationWorkspaceVersionView: [87],
            ConsultationWorkspaceVersionEdit: [89],
            // 結論頁屬於學員詳情／完成諮詢流程，需與工作台一起注入
            ConsultationWorkspaceConclusion: [87, 89]
        }
    }
};

/**
 * 權限操作類型常數
 */
export const PERMISSION_TYPES = {
    VIEW: 'view',
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    COPY: 'copy',
    GENERATE: 'generate',
    FINISH: 'finish',
    BATCH_UPLOAD: 'batchUpload',
    BATCH_DOWNLOAD: 'batchDownload'
};

/**
 * 根據頁面名稱獲取權限配置
 * @param {string} pageName - 頁面名稱
 * @returns {Object|null} 權限配置物件
 */
export function getPagePermissions(pageName) {
    return pagePermissionMap[pageName] || null;
}

/**
 * 根據模組路徑獲取模組權限配置
 * @param {string} modulePath - 模組路徑
 * @returns {Object|null} 模組權限配置物件
 */
export function getModulePermissions(modulePath) {
    return modulePermissionMap[modulePath] || null;
}
